import { useCallback, useEffect, useRef, useState } from "react";
import { calculateMetrics, DEMO_RESPONSES } from "../../shared/metrics";
import type { CatalogSnapshot, MetricsSnapshot } from "../../shared/model";
import { clientApi, liveSocketUrl } from "../api";

interface LiveMetricsState {
  metrics: MetricsSnapshot | null;
  connection: "connecting" | "live" | "reconnecting" | "offline";
  error: string | null;
  pulse: number;
}

export function useLiveMetrics(
  slug: string,
  demoCatalog?: CatalogSnapshot,
  frozen = false,
): LiveMetricsState {
  const [metrics, setMetrics] = useState<MetricsSnapshot | null>(() =>
    demoCatalog
      ? calculateMetrics("demo", "open", DEMO_RESPONSES, demoCatalog, 1, Date.now(), 1)
      : null,
  );
  const [connection, setConnection] = useState<LiveMetricsState["connection"]>(
    demoCatalog ? "offline" : "connecting",
  );
  const [error, setError] = useState<string | null>(null);
  const [pulse, setPulse] = useState(0);
  const previousVersion = useRef(metrics?.version ?? 0);
  const frozenRef = useRef(frozen);
  const pendingSnapshot = useRef<MetricsSnapshot | null>(null);

  const applySnapshot = useCallback((snapshot: MetricsSnapshot) => {
    if (previousVersion.current > 0 && snapshot.version > previousVersion.current) {
      setPulse((value) => value + 1);
    }
    previousVersion.current = snapshot.version;
    setMetrics(snapshot);
    setError(null);
  }, []);

  useEffect(() => {
    frozenRef.current = frozen;
    if (!frozen && pendingSnapshot.current) {
      applySnapshot(pendingSnapshot.current);
      pendingSnapshot.current = null;
    }
  }, [applySnapshot, frozen]);

  useEffect(() => {
    if (demoCatalog) return;
    let socket: WebSocket | null = null;
    let cancelled = false;
    let retryTimer: number | null = null;
    let attempts = 0;

    const receiveSnapshot = (snapshot: MetricsSnapshot) => {
      if (frozenRef.current) {
        pendingSnapshot.current = snapshot;
        setError(null);
        return;
      }
      applySnapshot(snapshot);
    };

    const connect = async () => {
      try {
        receiveSnapshot(await clientApi.getMetrics(slug));
      } catch (fetchError) {
        if (!cancelled) setError(fetchError instanceof Error ? fetchError.message : "NETWORK_ERROR");
      }
      if (cancelled) return;
      setConnection(attempts === 0 ? "connecting" : "reconnecting");
      socket = new WebSocket(liveSocketUrl(slug));
      socket.addEventListener("open", () => {
        attempts = 0;
        setConnection("live");
        socket?.send("snapshot");
      });
      socket.addEventListener("message", (event) => {
        try {
          const snapshot = JSON.parse(String(event.data)) as MetricsSnapshot;
          if (snapshot.type === "metrics") receiveSnapshot(snapshot);
        } catch {
          setError("INVALID_LIVE_MESSAGE");
        }
      });
      socket.addEventListener("close", () => {
        if (cancelled) return;
        attempts += 1;
        setConnection("reconnecting");
        retryTimer = window.setTimeout(connect, Math.min(1000 * 2 ** attempts, 10_000));
      });
      socket.addEventListener("error", () => socket?.close());
    };

    void connect();
    return () => {
      cancelled = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      socket?.close();
    };
  }, [applySnapshot, demoCatalog, slug]);

  return { metrics, connection, error, pulse };
}
